import { signal } from "@preact/signals";
import { useLocation } from "preact-iso";
import { useEffect, useRef } from "preact/hooks";
import { tierByName } from "../../constants.ts";
import { legalLinks, Logo } from "./components.tsx";
import { auth, useAuth, useQuery } from "./db.ts";
import { routes } from "./routes.tsx";
import { themeSignal, toggleTheme } from "./utils.ts";

export { Logo };

const navButtonBase =
  "w-full md:w-auto px-3 py-1.5 font-medium rounded-lg transition-all text-sm border cursor-pointer font-body";

const navButtonActive =
  `${navButtonBase} bg-accent hover:bg-accent-hover border-accent text-white`;

const navButtonInactive =
  `${navButtonBase} border-transparent bg-transparent text-text-primary-light dark:text-text-primary hover:border-accent/30 hover:bg-surface-raised-light dark:hover:bg-surface-raised`;

const signUpButtonActive =
  `${navButtonBase} bg-accent hover:bg-accent-hover border-accent text-white font-semibold`;

const Navigation = (
  { onNavigation, showSignOut = false, showUpgrade = false }: {
    onNavigation: () => void;
    showSignOut?: boolean;
    showUpgrade?: boolean;
  },
) => {
  const { route, url } = useLocation();
  return (
    <div className="flex flex-col md:flex-row w-full md:w-auto gap-2 md:gap-3">
      {routes.filter((x) => x.text).map(({ path, text }) => {
        const isActive = url === path;
        return (
          <div key={path} className="w-full md:w-auto">
            <button
              type="button"
              className={isActive ? navButtonActive : navButtonInactive}
              onClick={() => {
                onNavigation();
                route(path);
              }}
            >
              {text}
            </button>
          </div>
        );
      })}
      {showUpgrade && (
        <div className="w-full md:w-auto mt-2 md:mt-0">
          <button
            type="button"
            className={signUpButtonActive}
            onClick={() => {
              onNavigation();
              route("/upgrade");
            }}
          >
            Sign up
          </button>
        </div>
      )}
      {showSignOut && (
        <div className="w-full md:w-auto mt-2 md:mt-0">
          <button
            type="button"
            className={navButtonInactive}
            onClick={() => {
              auth.signOut();
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

const hamburgerOpen = signal(false);

const Hamburger = () => (
  <button
    type="button"
    className="inline-flex items-center justify-center p-3 rounded-lg text-text-secondary-light dark:text-text-secondary hover:bg-surface-overlay-light dark:hover:bg-surface-overlay focus:outline-none focus:ring-2 focus:ring-inset focus:ring-accent ml-2 transition-colors"
    aria-label="Open menu"
    onClick={() => hamburgerOpen.value = !hamburgerOpen.value}
  >
    <svg
      className="h-6 w-6"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 6h16M4 12h16M4 18h16"
      />
    </svg>
  </button>
);

const SunIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <circle cx="12" cy="12" r="5" />
    <path
      strokeLinecap="round"
      d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
    />
  </svg>
);

const MoonIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"
    />
  </svg>
);

const SystemIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 21h8M12 17v4" />
  </svg>
);

const ThemeToggle = () => {
  const current = themeSignal.value;
  return (
    <button
      type="button"
      className="p-2 rounded-lg border border-transparent bg-transparent text-text-secondary-light dark:text-text-secondary hover:border-accent/30 hover:bg-surface-raised-light dark:hover:bg-surface-raised hover:text-accent-light dark:hover:text-accent transition-colors cursor-pointer"
      onClick={toggleTheme}
      aria-label="Toggle light/dark mode"
      title={current === "light"
        ? "Theme: Light (click to switch to Dark)"
        : current === "dark"
        ? "Theme: Dark (click to switch to System)"
        : "Theme: System (click to switch to Light)"}
    >
      {current === "light"
        ? <SunIcon />
        : current === "dark"
        ? <MoonIcon />
        : <SystemIcon />}
    </button>
  );
};

const MobileNavbar = (
  { user, tier, billingPeriodStart }: {
    user: { email?: string | null; isGuest?: boolean } | null;
    tier: { name: string; emoji: string } | null;
    billingPeriodStart: string;
  },
) => {
  const { route } = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hamburgerOpen.value) return;
    const handleClick = (e: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        hamburgerOpen.value = false;
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [hamburgerOpen.value]);

  return (
    <div className="flex md:hidden items-center flex-grow">
      <div className="min-w-0 shrink truncate">
        <Logo />
      </div>
      <div className="flex-grow shrink-0" style={{ minWidth: 8 }} />
      <div className="flex items-center shrink-0 gap-0.5">
        <ThemeToggle />
        <div style={{ position: "relative" }} ref={menuRef}>
          <Hamburger />
          {hamburgerOpen.value && (
            <div className="absolute right-0 top-full mt-2 w-52 bg-surface-raised-light dark:bg-surface-raised rounded-xl shadow-lg border border-border-subtle-light dark:border-border-subtle z-50 animate-slide-down p-3">
              {user?.isGuest
                ? (
                  <button
                    type="button"
                    className="w-full text-left text-sm text-text-secondary-light dark:text-text-secondary px-2 py-1 mb-2 hover:text-accent-light dark:hover:text-accent transition-colors cursor-pointer"
                    onClick={() => {
                      route("/upgrade");
                      hamburgerOpen.value = false;
                    }}
                  >
                    Guest mode
                  </button>
                )
                : (
                  <div className="text-xs text-text-tertiary-light dark:text-text-tertiary px-2 py-1 mb-2">
                    <div>{user?.email}</div>
                    {tier && tier.name !== "Free" && (
                      <div>
                        <span className="font-semibold text-text-secondary-light dark:text-text-secondary">
                          {tier.emoji} {tier.name}
                        </span>
                        {" · "}
                        Period: {billingPeriodStart}
                      </div>
                    )}
                  </div>
                )}
              <Navigation
                onNavigation={() => {
                  hamburgerOpen.value = false;
                }}
                showSignOut
                showUpgrade={user?.isGuest}
              />
              <div className="border-t border-border-subtle-light dark:border-border-subtle mt-2 pt-2">
                {legalLinks.map(({ href, label }) => (
                  <a
                    key={href}
                    href={href}
                    className="block px-2 py-1 text-xs text-text-tertiary-light dark:text-text-tertiary hover:text-accent-light dark:hover:text-accent transition-colors"
                    onClick={() => {
                      hamburgerOpen.value = false;
                    }}
                  >
                    {label}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DesktopNavbar = () => (
  <div className="hidden md:flex flex-grow items-center justify-between px-4 gap-x-8">
    <Logo />
    <Navigation onNavigation={() => {}} />
  </div>
);

const Navbar = () => {
  const { user } = useAuth();
  const { data, isLoading } = useQuery(
    user?.id ? { $users: { $: { where: { id: user.id } } } } : null,
  );
  const tier = isLoading ? null : tierByName(data?.$users?.[0]?.tier ?? "");
  const billingPeriodStart = data?.$users?.[0]?.billingPeriodStart
    ? new Date(data.$users[0].billingPeriodStart).toLocaleDateString(
      "en-US",
      { day: "numeric", month: "short", year: "numeric" },
    )
    : "N/A";

  return (
    <>
      <MobileNavbar
        user={user ?? null}
        tier={tier}
        billingPeriodStart={billingPeriodStart}
      />
      <DesktopNavbar />
    </>
  );
};

const tierBadge =
  "text-xs font-medium px-2 py-0.5 rounded-full bg-accent-muted text-accent-light dark:text-accent border border-accent/20";

export const Header = () => {
  const { user, isLoading: authLoading } = useAuth();
  const { route } = useLocation();
  const { data, isLoading } = useQuery(
    user?.id ? { $users: { $: { where: { id: user.id } } } } : null,
  );
  const tier = isLoading ? null : tierByName(data?.$users?.[0]?.tier ?? "");
  if (authLoading || !user) {
    return (
      <div className="flex items-center gap-2">
        <Logo />
        <div style={{ flexGrow: 1 }} />
        <ThemeToggle />
      </div>
    );
  }
  return (
    <div className="flex">
      <Navbar />
      <div className="hidden md:flex" style={{ gap: 10, alignItems: "center" }}>
        {user?.isGuest
          ? (
            <button
              type="button"
              className="text-text-secondary-light dark:text-text-secondary text-sm hover:text-accent-light dark:hover:text-accent transition-colors cursor-pointer"
              onClick={() => route("/upgrade")}
            >
              Guest mode
            </button>
          )
          : (
            <div
              className="text-text-secondary-light dark:text-text-secondary text-sm"
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              {user?.email}
              {tier && tier.name !== "Free" && (
                <span className={tierBadge}>
                  {tier.emoji} {tier.name}
                </span>
              )}
            </div>
          )}
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <ThemeToggle />
          {user?.isGuest && (
            <button
              type="button"
              className={signUpButtonActive}
              onClick={() => {
                route("/upgrade");
              }}
            >
              Sign up
            </button>
          )}
          <button
            type="button"
            className={navButtonInactive}
            onClick={() => {
              auth.signOut();
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export const NotFound = () => (
  <div className="text-center py-20 text-text-secondary-light dark:text-text-secondary">
    Page not found
  </div>
);
